import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Plus, X, ArrowRight, Wand2, FileUp, Search, Loader2, BadgeEuro } from "lucide-react";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import MagicEnrichmentOverlay from "./MagicEnrichmentOverlay";

/**
 * FAB actions menu — Heetch Pro dark.
 * • Magic Prosperr : ouvre un sélecteur rapide de prospect → enrichit en 1 tap.
 * • Ajouter un prospect : ouvre AddProspectSheet via event.
 * • Importer une liste : navigue vers /import.
 */
export default function MagicProspectButton({ className = "" }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [magicSheet, setMagicSheet] = useState(false);

  const actions = [
    {
      label: "Magic Prosperr",
      sub: "Enrichir un lead avec les données publiques",
      icon: Wand2,
      testid: "fab-magic",
      iconCls: "text-emerald-300 bg-emerald-500/15 border-emerald-500/40",
      onClick: () => { setOpen(false); setMagicSheet(true); },
    },
    {
      label: "Ajouter un prospect",
      sub: "Saisie manuelle ou recherche par SIRET",
      icon: Plus,
      testid: "fab-add",
      iconCls: "text-sky-300 bg-sky-500/15 border-sky-500/40",
      onClick: () => { setOpen(false); window.dispatchEvent(new CustomEvent("openAddProspect")); },
    },
    {
      label: "Importer une liste",
      sub: "CSV, Excel ou vCard",
      icon: FileUp,
      testid: "fab-import",
      iconCls: "text-zinc-300 bg-[var(--surface-alt)] border-[var(--border)]",
      onClick: () => { setOpen(false); navigate("/import"); },
    },
  ];

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div className="fixed inset-0 z-40 bg-black/50" onClick={() => setOpen(false)} />
      )}

      {/* Action list */}
      {open && (
        <div className="fixed bottom-28 right-4 z-50 w-72 bg-[var(--surface)] border border-[var(--border)] shadow-floating rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-[var(--border-soft)] bg-[var(--surface-alt)]/50">
            <div className="hp-label">Actions rapides</div>
          </div>
          <div className="divide-y divide-[var(--border-soft)]">
            {actions.map((a) => {
              const Icon = a.icon;
              return (
                <button
                  key={a.testid}
                  onClick={a.onClick}
                  data-testid={a.testid}
                  className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-[var(--surface-alt)]/60 active:scale-[0.99] transition-colors"
                >
                  <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 border ${a.iconCls}`}>
                    <Icon className="w-4 h-4" strokeWidth={1.8} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-[var(--text)]">{a.label}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">{a.sub}</div>
                  </div>
                  <ArrowRight className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* FAB — Heetch Pro violet, lisible en dark */}
      <button
        onClick={() => setOpen((v) => !v)}
        className={`fixed bottom-20 right-4 z-50 w-14 h-14 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white grid place-items-center active:scale-95 transition-colors shadow-glow ${className}`}
        data-testid="magic-prosperr-fab"
        aria-label="Actions rapides"
      >
        {open
          ? <X className="w-5 h-5 transition-transform" strokeWidth={2.2} />
          : <Sparkles className="w-5 h-5 transition-transform" strokeWidth={1.8} />
        }
      </button>

      <MagicProspectSheet open={magicSheet} onClose={() => setMagicSheet(false)} navigate={navigate} />
    </>
  );
}

// ─── Magic Prosperr Sheet ─────────────────────────────────────────────────
function MagicProspectSheet({ open, onClose, navigate }) {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [enriching, setEnriching] = useState(null);
  const [quota, setQuota] = useState(null);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState(null);
  const [overlay, setOverlay] = useState(null); // { prospect, promise }

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      api.get("/prospects").then((r) => r.data || []).catch(() => []),
      api.get("/enrich/quota").then((r) => r.data).catch(() => null),
    ]).then(([list, q]) => {
      const sorted = list.slice().sort((a, b) => {
        const a1 = a.pappers_data ? 1 : 0;
        const b1 = b.pappers_data ? 1 : 0;
        if (a1 !== b1) return a1 - b1;
        return (b.score || 0) - (a.score || 0);
      });
      setProspects(sorted);
      setQuota(q);
      setLoading(false);
    });
  }, [open]);

  const filtered = prospects.filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (p.name || "").toLowerCase().includes(q) ||
      (p.company || "").toLowerCase().includes(q) ||
      (p.siret || "").includes(q)
    );
  });

  const eligibleForBulk = prospects.filter((p) => !p.pappers_data && (p.siret || "").length >= 9);

  const enrichOne = (p) => {
    setEnriching(p.prospect_id);
    const promise = api.post(`/enrich/${p.prospect_id}`)
      .then((r) => r.data)
      .catch((e) => { throw e; })
      .finally(() => setEnriching(null));
    setOverlay({ prospect: p, promise });
  };

  const startBulkConfirm = async () => {
    try {
      const { data } = await api.post("/enrich/bulk", { dry_run: true });
      setBulkConfirm(data);
    } catch {
      toast.error("Estimation impossible");
    }
  };

  const runBulk = async () => {
    setBulkRunning(true);
    try {
      const { data } = await api.post("/enrich/bulk", { max: 50 });
      const msg = `${data.applied} fiche${data.applied > 1 ? "s" : ""} enrichie${data.applied > 1 ? "s" : ""} · ${data.cost_eur.toFixed(2)} €`;
      if (data.budget_exhausted) toast.warning(`${msg} · budget mensuel atteint`);
      else if (data.applied === 0) toast.info("Aucune nouvelle donnée trouvée");
      else toast.success(msg);
      // Refresh
      const [list, q] = await Promise.all([
        api.get("/prospects").then((r) => r.data || []).catch(() => []),
        api.get("/enrich/quota").then((r) => r.data).catch(() => null),
      ]);
      setProspects(list);
      setQuota(q);
      setBulkConfirm(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action de masse échouée");
    } finally {
      setBulkRunning(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="bottom"
        className="rounded-t-2xl bg-[var(--surface)] border-t border-[var(--border)] text-[var(--text)] max-h-[85vh] overflow-hidden flex flex-col"
      >
        <SheetHeader className="pb-2 shrink-0">
          <SheetTitle className="text-left text-[var(--text)] flex items-center gap-2">
            <Wand2 className="w-4 h-4 text-emerald-400" strokeWidth={1.8} />
            Magic Prosperr
          </SheetTitle>
          <p className="text-xs text-zinc-500 leading-relaxed text-left">
            Sélectionnez un prospect : Prosperr récupère son chiffre d'affaires, ses dirigeants, ses effectifs et son historique financier auprès des bases publiques (Pappers, SIRENE).
          </p>
        </SheetHeader>

        {/* Cost / quota indicator */}
        {quota && quota.pappers_available && (
          <div className="px-1 mt-1 shrink-0">
            <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10">
              <BadgeEuro className="w-4 h-4 text-emerald-300 shrink-0" />
              <div className="flex-1 min-w-0 text-[11px] text-zinc-300 leading-tight">
                <span className="font-semibold text-white tabular-nums">{quota.cost_per_call_eur.toFixed(2)} €</span>
                <span className="text-zinc-400"> par enrichissement</span>
                {quota.monthly_budget_eur > 0 && (
                  <>
                    <span className="text-zinc-500"> · </span>
                    <span className="text-zinc-300">solde mensuel </span>
                    <span className="font-semibold text-white tabular-nums">{(quota.remaining_eur ?? 0).toFixed(2)} €</span>
                    <span className="text-zinc-500"> / {quota.monthly_budget_eur.toFixed(0)} €</span>
                  </>
                )}
              </div>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-300 tabular-nums shrink-0">
                {quota.calls_this_month} ce mois-ci
              </span>
            </div>
          </div>
        )}

        {/* Bulk action */}
        {eligibleForBulk.length > 1 && quota?.pappers_available && (
          <div className="px-1 mt-2 shrink-0">
            {!bulkConfirm ? (
              <button
                onClick={startBulkConfirm}
                data-testid="magic-bulk-trigger"
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-emerald-500/40 bg-emerald-500/15 hover:bg-emerald-500/25 transition-colors active:scale-[0.99]"
              >
                <div className="w-8 h-8 rounded-lg bg-emerald-500 grid place-items-center shrink-0 border border-emerald-500">
                  <Wand2 className="w-4 h-4 text-white" strokeWidth={1.8} />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <div className="text-[13px] font-semibold text-white">Enrichir {eligibleForBulk.length} prospects en une fois</div>
                  <div className="text-[11px] text-emerald-200/80 mt-0.5">Coût estimé · {(eligibleForBulk.length * (quota?.cost_per_call_eur || 0.15)).toFixed(2)} €</div>
                </div>
                <ArrowRight className="w-4 h-4 text-emerald-300 shrink-0" />
              </button>
            ) : (
              <div className="rounded-lg border border-emerald-500/50 bg-emerald-500/15 p-4">
                <div className="flex items-start gap-3">
                  <BadgeEuro className="w-4 h-4 text-emerald-300 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-white">Confirmer l'action de masse</div>
                    <div className="text-[11px] text-zinc-300 mt-1 leading-relaxed">
                      <strong className="text-white tabular-nums">{bulkConfirm.planned}</strong> fiches seront enrichies via Pappers pour un coût estimé de
                      <strong className="text-white tabular-nums"> {bulkConfirm.cost_eur.toFixed(2)} €</strong>.
                      {quota.monthly_budget_eur > 0 && (
                        <> Solde restant après opération : <strong className="text-white tabular-nums">{Math.max(0, (quota.remaining_eur ?? 0) - bulkConfirm.cost_eur).toFixed(2)} €</strong>.</>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => setBulkConfirm(null)}
                    disabled={bulkRunning}
                    data-testid="magic-bulk-cancel"
                    className="hp-btn hp-btn-secondary flex-1 h-10 text-xs"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={runBulk}
                    disabled={bulkRunning}
                    data-testid="magic-bulk-confirm"
                    className="hp-btn hp-btn-primary flex-1 h-10 text-xs"
                  >
                    {bulkRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                    {bulkRunning ? "Enrichissement…" : "Lancer maintenant"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Search */}
        <div className="px-1 mt-2 shrink-0">
          <div className="flex items-center gap-2 bg-[var(--surface-alt)] border border-[var(--border)] rounded-lg px-3 h-11">
            <Search className="w-4 h-4 text-zinc-500 shrink-0" />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Nom, entreprise, SIRET…"
              data-testid="magic-search"
              className="flex-1 bg-transparent outline-none text-sm text-[var(--text)] placeholder:text-zinc-500"
            />
            {search && (
              <button onClick={() => setSearch("")} className="text-zinc-500 hover:text-zinc-300 text-lg">×</button>
            )}
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto mt-3 pb-4 -mx-1 px-1">
          {loading ? (
            <div className="grid place-items-center py-10">
              <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-sm text-zinc-500">
              Aucun prospect ne correspond.
            </div>
          ) : (
            <div className="hp-card overflow-hidden divide-y divide-[var(--border-soft)]">
              {filtered.slice(0, 60).map((p) => {
                const enriched = !!p.pappers_data;
                const isLoading = enriching === p.prospect_id;
                return (
                  <button
                    key={p.prospect_id}
                    onClick={() => enrichOne(p)}
                    disabled={isLoading}
                    data-testid={`magic-pick-${p.prospect_id}`}
                    className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-[var(--surface-alt)]/60 transition-colors disabled:opacity-50"
                  >
                    <div className="w-9 h-9 rounded-lg bg-[var(--surface-alt)] border border-[var(--border)] grid place-items-center text-zinc-200 font-semibold text-sm shrink-0">
                      {(p.name || p.company || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] font-medium text-[var(--text)] truncate">{p.name || "Sans nom"}</div>
                      <div className="text-xs text-zinc-500 truncate">
                        {p.company || "—"}{p.siret ? ` · SIRET ${p.siret.slice(0, 9)}…` : ""}
                      </div>
                    </div>
                    {enriched ? (
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-md px-2 py-0.5 shrink-0">
                        Enrichi
                      </span>
                    ) : (
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-md px-2 py-0.5 shrink-0">
                        À enrichir
                      </span>
                    )}
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 text-emerald-400 animate-spin shrink-0" />
                    ) : (
                      <ArrowRight className="w-4 h-4 text-zinc-600 shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </SheetContent>

      {overlay && (
        <MagicEnrichmentOverlay
          prospect={overlay.prospect}
          enrichPromise={overlay.promise}
          onClose={() => setOverlay(null)}
          onViewProfile={() => {
            const pid = overlay.prospect.prospect_id;
            setOverlay(null);
            onClose();
            navigate(`/prospect/${pid}`);
          }}
        />
      )}
    </Sheet>
  );
}
